import mixpanel from "mixpanel-browser";
import ReactGA from "react-ga4";
import type { UserInfo } from "../contexts/AuthProvider";
import { localStorageService } from "../services/LocalStorageService";
import { isPublicCareersPath } from "./publicCareers";

interface StonlyWidget {
	(command: "addEventListener", eventName: string, handler: () => void): void;
	(command: "removeEventListener", eventName: string, handler: () => void): void;
	(command: "cleanup"): void;
	(command: "clearSentData"): void;
	(command: "identify", userId: string, properties?: { [key: string]: string | number | boolean | Date }): void;
	(command: "sendData", data: unknown): void;
	(command: "track", eventName: string): void;
}
declare global {
	interface Window {
		StonlyWidget?: StonlyWidget;
	}
}

const MIXPANEL_TOKEN = "c0aba9b04f1e8fbc7ad7877d7c69ae39";
const GA_MEASUREMENT_ID = "G-HY0DHJHLP4";
const env: string = import.meta.env.VITE_ENVIROMENT || "development";

export const setAnalyticsGlobalProperties = (props: Record<string, any>) => {
	globalProperties = { ...globalProperties, ...props };
};

import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";

const isApp = () => {
	return Capacitor.isNativePlatform();
};

export const getDeviceInfo = async () => {
	try {
		const info = await Device.getInfo();
		return {
			model: info.model,
			platform: info.platform,
			osVersion: info.osVersion,
			manufacturer: info.manufacturer,
		};
	} catch (error) {
		console.error("Failed to get device info:", error);
		return null;
	}
};

export const getAppVersion = async (): Promise<string> => {
	try {
		const info = await CapacitorApp.getInfo();
		return info.version;
	} catch (error) {
		console.error("Failed to get app version:", error);
		return "unknown";
	}
};

let globalProperties: Record<string, any> = {
	isApp: isApp(),
	appVersion: "unknown",
	deviceModel: null,
	devicePlatform: null,
	deviceOsVersion: null,
	deviceManufacturer: null,
};

/**
 * The identifier every analytics surface uses for a user: the `username` event property,
 * `mixpanel.identify`, and GA's `userId`. Routing all three through one helper keeps them from
 * drifting apart — a user identified one way but labelled another is unqueryable in Mixpanel.
 *
 * DEFAULT: phone-only users have an empty username. Without the fallback Mixpanel drops the
 * property outright and `identify` never fires, stranding them on an anonymous `$device:` id for
 * the whole session. `||` (not `??`) is deliberate: the empty case is `""` from the backend, not null.
 */
export const getAnalyticsIdentity = (userInfo: UserInfo | undefined): string | undefined => userInfo?.username || userInfo?.phone_number;

/**
 * Global properties that describe the currently authenticated user. They must be cleared on logout so a
 * different user logging in on the same tab never inherits the previous user's identity on events fired
 * before the next login refreshes them (e.g. events on the login screen). See `resetAnalyticsSession`.
 */
const USER_SCOPED_GLOBAL_PROPERTIES = ["user_id", "department_id", "user_role", "is_support_user", "impersonated_by"] as const;

/** @deprecated please use `const { register } = useAnalytics()` instead */
export const handleAnalyticsGlobalProperties = (user_data_response: UserInfo) => {
	setAnalyticsGlobalProperties({
		...globalProperties,
		user_id: user_data_response.id,
		department_id: localStorageService.getChosenDepartmentID() || user_data_response.departments?.map((d) => d.department_id).join(","),
		user_role: user_data_response.role,
		// Impersonation counts as support activity: the acting human is support even though the
		// user data describes the impersonated customer. Both cases must be filterable in Mixpanel.
		is_support_user: !!user_data_response.support || !!user_data_response.impersonated_by,
		// The impersonator's identifier, tagged onto every event so impersonation sessions are
		// attributable to the acting human. Null (not undefined) for real customer sessions so the
		// property is present and queryable in Mixpanel rather than silently absent.
		impersonated_by: user_data_response.impersonated_by ?? null,
	});
};

// Mirrors the canonical frontend-error convention in `useAnalytics().trackError` (contexts/analytics/
// hooks.ts): every error carries `__severity: "error"` so the weekly error report can query them
// together via `properties["__severity"] == "error"` (see .agents/skills/error-report/SKILL.md).
const SEVERITY_KEY = "__severity";
const ERROR_SEVERITY = "error";

/**
 * Report an analytics-teardown failure through the canonical error helper so it lands in frontend
 * telemetry (Mixpanel/GA), while guaranteeing that a broken analytics client can never turn logout into
 * a failure — the report itself is guarded and falls back to `console.error`.
 *
 * This runs outside React (logout is not a component), so the `useAnalytics().trackError` hook is
 * unavailable; the legacy `trackErrorEvent` is tagged with `__severity: "error"` to match that hook's
 * queryable shape instead. The descriptive event name is kept (as `trackError` call sites do) so each
 * teardown step stays distinguishable.
 */
const reportAnalyticsTeardownFailure = (event: string, error: unknown) => {
	try {
		trackErrorEvent(event, error, { [SEVERITY_KEY]: ERROR_SEVERITY });
	} catch (reportError) {
		console.error(event, error, reportError);
	}
};

/**
 * Reset all analytics session state on logout. Without this, a user who logs out and logs in as a
 * *different* user in the same tab (no full reload) keeps the previous user's session replay and
 * identity, so the new session's replay and events are misattributed. `reset()` mints a fresh Mixpanel
 * distinct_id and clears super properties; GA4's `user_id` is set independently (see App's identify
 * effect) and react-ga4's reset does not clear it, so it is cleared on its own. That login then
 * re-identifies and the inactivity hook starts a fresh recording. See APP-697.
 */
export const resetAnalyticsSession = () => {
	for (const key of USER_SCOPED_GLOBAL_PROPERTIES) {
		delete globalProperties[key];
	}

	// Cleared independently of the Mixpanel teardown below so a Mixpanel failure can't leave GA still
	// attributing events to the logged-out user.
	try {
		ReactGA.set({ userId: null });
	} catch (error) {
		reportAnalyticsTeardownFailure("Failed to reset GA identity on logout", error);
	}

	try {
		mixpanel.stop_session_recording();
		mixpanel.reset();
		// reset() clears registered super properties too, so restore `env` — auto-tracked pageviews on
		// the login screen (before the next login re-registers it) would otherwise lose it.
		mixpanel.register({ env });
	} catch (error) {
		reportAnalyticsTeardownFailure("Failed to reset analytics session on logout", error);
	}
};

export const initAnalytics = () => {
	mixpanel.init(MIXPANEL_TOKEN, {
		debug: false,
		track_pageview: true,
		persistence: "localStorage",
		record_sessions_percent: 0,
		record_heatmap_data: true,
		record_mask_text_selector: "input[type='password'], input[name='password']",
		record_block_selector: "input[type='password'], input[name='password']",
	});
	mixpanel.register({ env: env });
	if (isGoogleAnalyticsAllowed()) ReactGA.initialize(GA_MEASUREMENT_ID);
};

/**
 * Google Analytics never loads on the public careers page: applicants are not our users, and the page is often shown
 * inside a customer's website, whose visitors did not agree to our trackers. Mixpanel still counts the page's own
 * anonymous events. Read once at start-up: the page is only ever opened directly, never navigated to from the app.
 */
export const isGoogleAnalyticsAllowed = (): boolean =>
	// DEFAULT: with no `window` (a unit test importing this module outside a DOM) there is no page path and no careers
	// page to keep GA off; the start-up `initialize` then runs exactly as it did before this gate.
	typeof window === "undefined" || !isPublicCareersPath(window.location.pathname);

/** @deprecated please use `const { track } = useAnalytics()` instead */
export const trackEvent = (event: string, properties?: Record<string, any>) => {
	if (!shouldTrack()) {
		return;
	}

	const allProps = { ...globalProperties, ...properties, env };
	mixpanel.track(event, allProps);
	ReactGA.event(event, allProps);

	window.StonlyWidget?.("track", event);
	window.StonlyWidget?.("sendData", { [event]: allProps });
};

/** @deprecated please use `const { trackError } = useAnalytics()` instead */
export const trackErrorEvent = (event: string, error: any, properties?: Record<string, any>) => {
	if (shouldTrack()) {
		const error_message = typeof error === "string" ? error : typeof error?.message === "string" ? error.message : "Unknown error";
		const error_code = typeof error === "string" ? "string_error" : typeof error?.code === "string" ? error.code : "unknown";
		const allProps = { ...globalProperties, ...properties, env, error_message, error_code };

		mixpanel.track(event, allProps);
		ReactGA.event(event, allProps);

		window.StonlyWidget?.("track", event);
		window.StonlyWidget?.("sendData", { [event]: allProps });

		console.error(error_message, error);
	}
};

/** @deprecated please use `const { setUser } = useAnalytics()` instead */
export const setUserProps = (props: any): void => {
	window.StonlyWidget?.("identify", `userId:${props.userId}@deptId:${props.departmentId}`, {
		"user-id": props.userId,
		"department-id": props.departmentId,
		env,
	});
	mixpanel.people.set(props);
};

export const shouldTrack = () => {
	const env: string = import.meta.env.VITE_ENVIROMENT || "development";

	// Only track in production by default
	if (env === "production") {
		return true;
	}

	// Allow debugging override via localStorage or URL param
	const debugOverride =
		localStorage.getItem("enable_mixpanel_debug") === "true" || new URLSearchParams(window.location.search).get("mixpanel_debug") === "true";

	return debugOverride;
};
